import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BlendSheet from './BlendSheet';
import DailyBlendSummary from './DailyBlendSummary';
import LoadingSpinner from '../LoadingSpinner';
import './BlendManager.css';

function BlendManager({ batch }) {
  const [blends, setBlends] = useState([]);
  const [melters, setMelters] = useState([]);
  const [selectedMelter, setSelectedMelter] = useState('');
  const [blendDate, setBlendDate] = useState(new Date().toISOString().split('T')[0]);
  const [operatorName, setOperatorName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBlend, setSelectedBlend] = useState(null);
  const [showBlendSheet, setShowBlendSheet] = useState(false);
  const [showDailySummary, setShowDailySummary] = useState(false);
  const [melterSuggestions, setMelterSuggestions] = useState([]);
  const [remainingLbs, setRemainingLbs] = useState({ wax: 0, oil: 0, total: 0 });
  const [blendWeight, setBlendWeight] = useState('');
  const [usePartialAllocation, setUsePartialAllocation] = useState(false);
  const [yieldPercentage, setYieldPercentage] = useState('0.97');

  useEffect(() => {
    if (batch?.id) {
      fetchBlends();
      fetchMelters();
    }
  }, [batch?.id]);

  useEffect(() => {
    // Fetch default yield percentage from settings
    const fetchYieldSetting = async () => {
      try {
        const response = await axios.get('/api/settings/blend_yield_percentage');
        setYieldPercentage(response.data.setting_value);
      } catch (err) {
        console.error('Failed to fetch yield setting:', err);
        // Keep default value of 0.97
      }
    };
    fetchYieldSetting();
  }, []);

  useEffect(() => {
    if (batch?.total_wax_lbs && melters.length > 0) {
      fetchMelterSuggestions();
    }
  }, [batch?.total_wax_lbs, melters]);

  const fetchBlends = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/batches/${batch.id}/blends`);
      setBlends(response.data);
      setError(null);

      // Calculate remaining lbs
      await calculateRemainingLbs();
    } catch (err) {
      console.error('Error fetching blends:', err);
      setError('Failed to load blends');
    } finally {
      setLoading(false);
    }
  };

  const calculateRemainingLbs = async () => {
    try {
      const response = await axios.get(`/api/batches/${batch.id}/blends`);
      const blends = response.data;

      let allocatedWax = 0;
      let allocatedOil = 0;

      blends.forEach(blend => {
        allocatedWax += parseFloat(blend.total_wax_lbs || 0);
        allocatedOil += parseFloat(blend.total_oil_lbs || 0);
      });

      const remainingWax = parseFloat(batch.total_wax_lbs) - allocatedWax;
      const remainingOil = parseFloat(batch.total_oil_lbs) - allocatedOil;
      const remainingTotal = remainingWax + remainingOil;

      setRemainingLbs({
        wax: Math.max(0, remainingWax).toFixed(2),
        oil: Math.max(0, remainingOil).toFixed(2),
        total: Math.max(0, remainingTotal).toFixed(2)
      });

      // Set default blend weight to remaining total
      if (!blendWeight && remainingTotal > 0) {
        setBlendWeight(remainingTotal.toFixed(2));
      }
    } catch (err) {
      console.error('Error calculating remaining lbs:', err);
    }
  };

  // Calculate wax and oil from total blend weight (9.090909% oil)
  const calculateWaxAndOil = (totalWeight) => {
    const total = parseFloat(totalWeight) || 0;
    const oilPercentage = 0.09090909;
    const oil = (total * oilPercentage).toFixed(2);
    const wax = (total * (1 - oilPercentage)).toFixed(2);
    return { wax: parseFloat(wax), oil: parseFloat(oil) };
  };

  const fetchMelters = async () => {
    try {
      const response = await axios.get('/api/melters');
      setMelters(response.data);

      // Auto-select first suitable melter if none selected
      if (response.data.length > 0 && !selectedMelter) {
        const suitable = response.data.find(m =>
          parseFloat(m.capacity_lbs) >= parseFloat(batch.total_wax_lbs)
        );
        if (suitable) {
          setSelectedMelter(suitable.id.toString());
        }
      }
    } catch (err) {
      console.error('Error fetching melters:', err);
    }
  };

  const fetchMelterSuggestions = async () => {
    try {
      // Don't show suggestions based on full batch - wait for user to enter blend weight
      const response = await axios.get('/api/melters');
      setMelterSuggestions(response.data);
    } catch (err) {
      console.error('Error fetching melter suggestions:', err);
    }
  };

  const generateBlend = async () => {
    if (!selectedMelter) {
      alert('Please select a melter');
      return;
    }

    if (!blendWeight || parseFloat(blendWeight) <= 0) {
      alert('Please enter a valid blend weight');
      return;
    }

    const totalWeight = parseFloat(blendWeight);
    const maxTotal = parseFloat(remainingLbs.total);

    if (totalWeight > maxTotal) {
      alert(`Blend weight (${totalWeight} lbs) exceeds remaining (${maxTotal} lbs)`);
      return;
    }

    setGenerating(true);
    try {
      // Calculate wax and oil from total weight
      const { wax, oil } = calculateWaxAndOil(blendWeight);

      const blendData = {
        melter_id: parseInt(selectedMelter),
        blend_date: blendDate,
        operator_name: operatorName || null,
        notes: notes || null,
        yield_percentage: parseFloat(yieldPercentage)
      };

      // Add custom amounts if not using full batch
      if (usePartialAllocation || totalWeight < maxTotal) {
        blendData.custom_wax_lbs = wax;
        blendData.custom_oil_lbs = oil;
      }

      const response = await axios.post(`/api/batches/${batch.id}/generate-blend`, blendData);

      // Add new blend to list
      setBlends([response.data, ...blends]);

      // Open PDF directly in new tab
      const pdfUrl = `/api/blends/${response.data.id}/pdf?batchId=${batch.id}`;
      window.open(pdfUrl, '_blank');

      // Clear form
      setNotes('');
      setBlendWeight('');
      setUsePartialAllocation(false);

      // Recalculate remaining
      await calculateRemainingLbs();

      alert(`Blend ${response.data.blend_number} generated successfully!`);
    } catch (err) {
      console.error('Error generating blend:', err);
      alert('Failed to generate blend: ' + (err.response?.data?.error || err.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleStatusChange = async (blendId, newStatus) => {
    try {
      await axios.patch(`/api/blends/${blendId}/status`, { status: newStatus });
      await fetchBlends();
    } catch (err) {
      console.error('Error updating blend status:', err);
      alert('Failed to update blend status');
    }
  };

  const handleDeleteBlend = async (blendId) => {
    if (!window.confirm('Are you sure you want to delete this blend?')) {
      return;
    }

    try {
      await axios.delete(`/api/blends/${blendId}`);
      await fetchBlends();
    } catch (err) {
      console.error('Error deleting blend:', err);
      alert('Failed to delete blend');
    }
  };

  const handlePrintBlend = (blend) => {
    setSelectedBlend(blend);
    setShowBlendSheet(true);
  };

  const handleOpenPdf = (blend) => {
    const pdfUrl = `/api/blends/${blend.id}/pdf?batchId=${batch.id}`;
    window.open(pdfUrl, '_blank');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return '#999';
      case 'in_production':
        return '#ff9800';
      case 'completed':
        return '#4caf50';
      default:
        return '#666';
    }
  };

  const getSelectedMelter = () => {
    return melters.find(m => m.id.toString() === selectedMelter);
  };

  const calculateWaxInches = () => {
    const melter = getSelectedMelter();
    if (!melter || !blendWeight) return 0;

    const { wax } = calculateWaxAndOil(blendWeight);
    return (parseFloat(wax) / parseFloat(melter.lbs_per_inch)).toFixed(2);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={fetchBlends}>Retry</button>
      </div>
    );
  }

  const selectedMelterData = getSelectedMelter();
  const waxInches = calculateWaxInches();

  return (
    <div className="blend-manager">
      <div className="blend-generation-section">
        <h4>Generate Blend</h4>

        <div className="blend-info-cards">
          <div className="info-card">
            <div className="info-label">Batch Total Weight</div>
            <div className="info-value">{(parseFloat(batch.total_wax_lbs) + parseFloat(batch.total_oil_lbs)).toFixed(2)} lbs</div>
          </div>
          <div className="info-card">
            <div className="info-label">Batch Wax</div>
            <div className="info-value">{batch.total_wax_lbs} lbs</div>
          </div>
          <div className="info-card">
            <div className="info-label">Batch Oil</div>
            <div className="info-value">{batch.total_oil_lbs} lbs</div>
          </div>
          <div className="info-card">
            <div className="info-label">Remaining Weight</div>
            <div className="info-value" style={{ color: parseFloat(remainingLbs.total) > 0 ? '#ff9800' : '#4caf50' }}>
              {remainingLbs.total} lbs
            </div>
          </div>
          <div className="info-card">
            <div className="info-label">Remaining Wax</div>
            <div className="info-value" style={{ color: parseFloat(remainingLbs.wax) > 0 ? '#ff9800' : '#4caf50' }}>
              {remainingLbs.wax} lbs
            </div>
          </div>
          <div className="info-card">
            <div className="info-label">Remaining Oil</div>
            <div className="info-value" style={{ color: parseFloat(remainingLbs.oil) > 0 ? '#ff9800' : '#4caf50' }}>
              {remainingLbs.oil} lbs
            </div>
          </div>
        </div>

        <div className="blend-form">
          <div className="form-row">
            <div className="form-group">
              <label>Select Melter *</label>
              <select
                value={selectedMelter}
                onChange={(e) => setSelectedMelter(e.target.value)}
                className="melter-select"
              >
                <option value="">Choose a melter...</option>
                {melterSuggestions.map(melter => {
                  // Calculate suitability based on entered blend weight, not batch total
                  const blendWax = blendWeight ? calculateWaxAndOil(blendWeight).wax : 0;
                  const utilization = blendWax > 0 ? ((blendWax / parseFloat(melter.capacity_lbs)) * 100).toFixed(1) : 0;
                  const isSuitable = blendWax === 0 || blendWax <= parseFloat(melter.capacity_lbs);
                  const isOptimal = blendWax > 0 && blendWax >= parseFloat(melter.capacity_lbs) * 0.5 && blendWax <= parseFloat(melter.capacity_lbs) * 0.9;

                  return (
                    <option
                      key={melter.id}
                      value={melter.id}
                      className={isOptimal ? 'optimal' : isSuitable ? '' : 'unsuitable'}
                    >
                      {melter.name} - {melter.capacity_lbs} lbs capacity
                      {blendWeight && ` (${utilization}% utilization`}
                      {isOptimal && ' - RECOMMENDED'}
                      {!isSuitable && ' - TOO SMALL'}
                      {blendWeight && ')'}
                    </option>
                  );
                })}
              </select>
            </div>

          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Blend Weight (lbs) *</label>
              <input
                type="number"
                step="0.01"
                value={blendWeight}
                onChange={(e) => setBlendWeight(e.target.value)}
                placeholder={`Max: ${remainingLbs.total} lbs`}
                className="text-input"
                max={remainingLbs.total}
              />
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                Available: {remainingLbs.total} lbs
                {blendWeight && ` | Wax: ${calculateWaxAndOil(blendWeight).wax} lbs | Oil: ${calculateWaxAndOil(blendWeight).oil} lbs`}
              </small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Yield Percentage</label>
              <input
                type="number"
                step="0.01"
                min="0.01"
                max="1"
                value={yieldPercentage}
                onChange={(e) => setYieldPercentage(e.target.value)}
                className="text-input"
              />
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                {(parseFloat(yieldPercentage || 0) * 100).toFixed(2)}% yield (accounts for {(100 - parseFloat(yieldPercentage || 0) * 100).toFixed(2)}% loss)
              </small>
            </div>
          </div>

          {selectedMelterData && blendWeight && (
            <div className="melter-details" style={{ marginTop: '10px' }}>
              <div className="melter-info">
                <span className="label">Tank Capacity:</span>
                <span className="value">{selectedMelterData.capacity_lbs} lbs</span>
              </div>
              <div className="melter-info">
                <span className="label">Wax Height:</span>
                <span className="value highlight">{waxInches} inches</span>
              </div>
              <div className="melter-info">
                <span className="label">Oil Weight:</span>
                <span className="value">{calculateWaxAndOil(blendWeight).oil} lbs</span>
              </div>
              <div className="melter-info">
                <span className="label">Utilization:</span>
                <span className="value">
                  {((calculateWaxAndOil(blendWeight).wax / parseFloat(selectedMelterData.capacity_lbs)) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Blend Date</label>
              <input
                type="date"
                value={blendDate}
                onChange={(e) => setBlendDate(e.target.value)}
                className="date-input"
              />
            </div>

            <div className="form-group">
              <label>Operator Name</label>
              <input
                type="text"
                value={operatorName}
                onChange={(e) => setOperatorName(e.target.value)}
                placeholder="Enter operator name"
                className="text-input"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <label>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this blend..."
                className="notes-input"
                rows="3"
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              onClick={generateBlend}
              className="generate-btn"
              disabled={generating || !selectedMelter}
            >
              {generating ? 'Generating...' : 'Generate Blend'}
            </button>
            <button
              onClick={() => setShowDailySummary(true)}
              className="summary-btn"
            >
              Daily Summary
            </button>
          </div>
        </div>
      </div>

      {blends.length > 0 && (
        <div className="blends-list-section">
          <h4>Generated Blends ({blends.length})</h4>

          <table className="blends-table">
            <thead>
              <tr>
                <th>Blend Number</th>
                <th>Date</th>
                <th>Melter</th>
                <th>Wax (lbs/in)</th>
                <th>Oil (lbs)</th>
                <th>Yield</th>
                <th>Status</th>
                <th>Operator</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {blends.map(blend => (
                <tr key={blend.id}>
                  <td className="blend-number">{blend.blend_number}</td>
                  <td>{new Date(blend.blend_date).toLocaleDateString()}</td>
                  <td>{blend.melters?.name || 'N/A'}</td>
                  <td>
                    {blend.total_wax_lbs} lbs /
                    <span className="highlight"> {blend.wax_inches || 'N/A'} in</span>
                  </td>
                  <td>{blend.total_oil_lbs} lbs</td>
                  <td>{blend.yield_percentage ? `${(parseFloat(blend.yield_percentage) * 100).toFixed(1)}%` : '97%'}</td>
                  <td>
                    <select
                      value={blend.status}
                      onChange={(e) => handleStatusChange(blend.id, e.target.value)}
                      className="status-select"
                      style={{ borderColor: getStatusColor(blend.status) }}
                    >
                      <option value="draft">Draft</option>
                      <option value="in_production">In Production</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                  <td>{blend.operator_name || '-'}</td>
                  <td className="blend-actions">
                    <button
                      onClick={() => handleOpenPdf(blend)}
                      className="action-btn pdf-btn"
                      title="Open PDF in new tab"
                    >
                      PDF
                    </button>
                    <button
                      onClick={() => handlePrintBlend(blend)}
                      className="action-btn print-btn"
                      title="View Blend Sheet"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDeleteBlend(blend.id)}
                      className="action-btn delete-btn"
                      title="Delete Blend"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showBlendSheet && selectedBlend && (
        <BlendSheet
          blend={selectedBlend}
          batch={batch}
          onClose={() => {
            setShowBlendSheet(false);
            setSelectedBlend(null);
          }}
        />
      )}

      {showDailySummary && (
        <DailyBlendSummary
          date={blendDate}
          onClose={() => setShowDailySummary(false)}
        />
      )}
    </div>
  );
}

export default BlendManager;